import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Link,
    Paper,
    Switch,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GoogleLoginButton from 'react-social-login-buttons/lib/buttons/GoogleLoginButton';
import MicrosoftLoginButton from 'react-social-login-buttons/lib/buttons/MicrosoftLoginButton';
import { LoginSocialGoogle } from 'reactjs-social-login';
import { startMicrosoftSignIn, hasPendingMicrosoftSignIn, takeMicrosoftSignIn } from "utils/microsoftSignIn";
import { fixGoogleUserinfoProxy } from "utils/fixGoogleUserinfoProxy";
import axiosConfig from "restApi/axiosConfig";
import userApi from "restApi/userApi";
import {
    enablePushNotifications,
    disablePushNotifications,
    getPushStatus,
} from "utils/webPush";
import { UserContext } from '../../contexts/UserContext';
import { buildUser } from "utils/user";
import { teamLabel } from "utils/team";

fixGoogleUserinfoProxy();

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: "100vh",
    },
    section: {
        margin: theme.spacing(4, "auto"),
        maxWidth: 640,
        padding: theme.spacing(3, 4),
    },
    avatar: {
        width: 64,
        height: 64,
        marginRight: theme.spacing(2),
        backgroundColor: theme.palette.secondary.main,
        fontSize: 24,
    },
    header: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
    roleChip: {
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(0.5),
    },
    socialButton: {
        marginBottom: theme.spacing(1.5),
    },
}));

const initials = (firstName, lastName) => {
    const first = (firstName || "").charAt(0);
    const last = (lastName || "").charAt(0);
    return (first + last).toUpperCase() || "?";
};

const AccountSettings = () => {
    const classes = useStyles();
    const userContext = React.useContext(UserContext);
    const [profile, setProfile] = React.useState(null);
    const [team, setTeam] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [confirmEraseOpen, setConfirmEraseOpen] = React.useState(false);
    const [erasing, setErasing] = React.useState(false);
    const [eraseError, setEraseError] = React.useState("");
    const [eraseEmailSent, setEraseEmailSent] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [exportError, setExportError] = React.useState("");
    const [confirmLeaveOpen, setConfirmLeaveOpen] = React.useState(false);
    const [leaving, setLeaving] = React.useState(false);
    const [leaveError, setLeaveError] = React.useState("");
    const [socialApps, setSocialApps] = React.useState({});
    const [linkStatus, setLinkStatus] = React.useState(null);
    const [vapidPublicKey, setVapidPublicKey] = React.useState("");
    // unsupported | denied | subscribed | not-subscribed | null (still
    // loading) - see utils/webPush.js.
    const [pushStatus, setPushStatus] = React.useState(null);
    const [pushBusy, setPushBusy] = React.useState(false);
    const [pushError, setPushError] = React.useState("");

    const linkProvider = (provider, accessToken) => {
        if (!accessToken) return;
        axiosConfig
            .post(`/auth/${provider}/`, { access_token: accessToken })
            .then(() => setLinkStatus({ severity: "success", provider }))
            .catch((err) => {
                const detail =
                    err && err.response && err.response.data && err.response.data.detail;
                setLinkStatus({ severity: "error", provider, detail });
            });
    };

    // Back from Microsoft's sign-in (utils/microsoftSignIn.js): link it.
    React.useEffect(() => {
        if (!socialApps.microsoft || !hasPendingMicrosoftSignIn()) return;
        takeMicrosoftSignIn(socialApps.microsoft)
            .then((data) => linkProvider("microsoft", data.access_token))
            .catch(() => setLinkStatus({ severity: "error", provider: "microsoft" }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socialApps.microsoft]);

    // Step 1 of erasing your personal data: asks the server to email a
    // confirmation link (ErasureRequestView in api/views.py). Nothing is
    // erased until that link is opened and confirmed (/confirm-erasure).
    const requestErasure = () => {
        setErasing(true);
        setEraseError("");
        axiosConfig
            .post("/api/users/erase/request/")
            .then(() => {
                setEraseEmailSent(true);
                setConfirmEraseOpen(false);
            })
            .catch((err) => {
                setEraseError(
                    (err.response && err.response.data && err.response.data.detail) ||
                        "Something went wrong - try again."
                );
            })
            .finally(() => setErasing(false));
    };

    // Downloads a JSON copy of the data held about you (DataExportView in
    // api/views.py). Fetched through axios so the auth header is sent, then
    // saved from an in-memory blob.
    const downloadMyData = () => {
        setExporting(true);
        setExportError("");
        axiosConfig
            .get("/api/users/export/", { responseType: "blob" })
            .then((response) => {
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = "planpraise-my-data.json";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => setExportError("Couldn't prepare your download - try again in a moment."))
            .finally(() => setExporting(false));
    };

    // Leaves the active team only - the account (and anything you made)
    // stays. LeaveTeam in api/views.py refuses for the last team leader.
    const leaveTeam = () => {
        setLeaving(true);
        setLeaveError("");
        axiosConfig
            .post("/api/team/leave/")
            .then((res) => {
                const updated = buildUser(res.data, true);
                localStorage.setItem("user", JSON.stringify(updated));
                userContext.setUser(updated);
                // Everything else in the app is scoped to the active team and
                // fetched once on mount - same reason TeamSwitcher hard-reloads.
                window.location.reload();
            })
            .catch((err) => {
                setLeaving(false);
                setLeaveError(
                    (err.response && err.response.data && err.response.data.detail) ||
                        "Couldn't leave the team - try again."
                );
            });
    };

    const refreshPushStatus = () => getPushStatus().then(setPushStatus);

    const togglePushNotifications = () => {
        setPushError("");
        setPushBusy(true);
        const action =
            pushStatus === "subscribed"
                ? disablePushNotifications()
                : enablePushNotifications(vapidPublicKey);
        action
            .catch((err) => setPushError(err.message || "Something went wrong - try again."))
            .finally(() => {
                refreshPushStatus();
                setPushBusy(false);
            });
    };

    React.useEffect(() => {
        axiosConfig.get("/api/config/").then((res) => {
            setSocialApps(res.data.socialApps || {});
            setVapidPublicKey(res.data.vapidPublicKey || "");
        });
        refreshPushStatus();
        userApi.getCurrentUser().then((res) => {
            setProfile(res.data);
            if (res.data.team) {
                axiosConfig
                    .get(`/api/teams/${res.data.team}/`)
                    .then((teamRes) => setTeam(teamRes.data))
                    .catch(() => setTeam(null))
                    .finally(() => setLoading(false));
            } else {
                setLoading(false);
            }
        }).catch(() => setLoading(false));
    }, []);

    return (
        <Grid container component="main" className={classes.root}>
            <Grid item xs={12}>
                <Paper className={classes.section}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : profile ? (
                        <>
                            <div className={classes.header}>
                                <Avatar className={classes.avatar}>
                                    {initials(profile.first_name, profile.last_name)}
                                </Avatar>
                                <div>
                                    <Typography variant="h6">
                                        {profile.first_name || profile.last_name
                                            ? `${profile.first_name} ${profile.last_name}`.trim()
                                            : profile.email}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {profile.email}
                                    </Typography>
                                </div>
                            </div>
                            <Box mb={team ? 2 : 0}>
                                {profile.is_team_leader && (
                                    <Chip
                                        className={classes.roleChip}
                                        size="small"
                                        color="primary"
                                        label="Team Leader"
                                    />
                                )}
                                {profile.is_team_admin && (
                                    <Chip
                                        className={classes.roleChip}
                                        size="small"
                                        color="primary"
                                        label="Team Admin"
                                    />
                                )}
                            </Box>

                            {team && (
                                <>
                                    <Divider />
                                    <Box mt={2}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Team
                                        </Typography>
                                        <Typography variant="body1">
                                            {teamLabel(team)}
                                            {team.city ? `, ${team.city}` : ""}
                                        </Typography>
                                        <Box mt={1}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setLeaveError("");
                                                    setConfirmLeaveOpen(true);
                                                }}
                                            >
                                                Leave team
                                            </Button>
                                        </Box>
                                    </Box>
                                </>
                            )}

                            {!team && (
                                <Box mt={2}>
                                    <Typography variant="body2" color="textSecondary">
                                        You're not on a team right now. Ask a team leader for an
                                        invitation link to join one.
                                    </Typography>
                                </Box>
                            )}

                            <Box mt={2}>
                                <Typography variant="body2" color="textSecondary">
                                    Leading another worship team?{" "}
                                    <Link component={RouterLink} to="/request-access">
                                        Request access
                                    </Link>
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Typography color="error">
                            Couldn't load your profile. Try refreshing the page.
                        </Typography>
                    )}
                </Paper>

                {profile && (
                    <Paper className={classes.section}>
                        <Typography variant="subtitle1" gutterBottom>
                            Notifications
                        </Typography>
                        {pushStatus === "unsupported" ? (
                            <Typography variant="body2" color="textSecondary">
                                Push notifications aren't supported in this browser.
                            </Typography>
                        ) : pushStatus === "denied" ? (
                            <Typography variant="body2" color="textSecondary">
                                Notifications are blocked for this site in your browser's
                                settings - turn them back on there to enable this.
                            </Typography>
                        ) : (
                            <>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="secondary"
                                            checked={pushStatus === "subscribed"}
                                            disabled={pushBusy || pushStatus === null}
                                            onChange={togglePushNotifications}
                                        />
                                    }
                                    label="Push notifications for new or updated events"
                                />
                                {pushError && (
                                    <Typography variant="body2" color="error">
                                        {pushError}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>
                )}

                {profile && (socialApps.google || socialApps.microsoft) && (
                    <Paper className={classes.section}>
                        <Typography variant="subtitle1" gutterBottom>
                            Linked sign-in methods
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Add another way to sign in to this account.
                        </Typography>
                        {socialApps.google && (
                            <LoginSocialGoogle
                                className={classes.socialButton}
                                client_id={socialApps.google}
                                scope="openid profile email"
                                discoveryDocs="claims_supported"
                                access_type="offline"
                                onResolve={({ data }) => linkProvider("google", data.access_token)}
                                onReject={() => setLinkStatus({ severity: "error", provider: "google" })}
                            >
                                <GoogleLoginButton text="Link Google account" />
                            </LoginSocialGoogle>
                        )}
                        {socialApps.microsoft && (
                            <div className={classes.socialButton}>
                                <MicrosoftLoginButton
                                    text="Link Microsoft account"
                                    onClick={() =>
                                        startMicrosoftSignIn({ clientId: socialApps.microsoft }).catch(() =>
                                            setLinkStatus({ severity: "error", provider: "microsoft" })
                                        )
                                    }
                                />
                            </div>
                        )}
                        {linkStatus && linkStatus.severity === "success" && (
                            <Typography variant="body2" color="primary">
                                {linkStatus.provider === "google" ? "Google" : "Microsoft"} account linked.
                            </Typography>
                        )}
                        {linkStatus && linkStatus.severity === "error" && (
                            <Typography variant="body2" color="error">
                                {linkStatus.detail || "Couldn't link that account. Try again."}
                            </Typography>
                        )}
                    </Paper>
                )}

                {profile && (
                    <Paper className={classes.section}>
                        <Typography variant="subtitle1" gutterBottom>
                            Your data
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Download a copy of the personal data we hold about you: your
                            profile, teams, the songs you added and your votes.
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={downloadMyData}
                            disabled={exporting}
                            style={{ marginBottom: 16 }}
                        >
                            {exporting ? "Preparing..." : "Download my data"}
                        </Button>
                        {exportError && (
                            <Typography variant="body2" color="error" paragraph>
                                {exportError}
                            </Typography>
                        )}
                        <Typography variant="body2" color="textSecondary" paragraph>
                            You can ask us to erase your personal data. Your name, email and
                            sign-in details are permanently removed and you're taken off every
                            team. Songs your team added stay, without your name.
                        </Typography>
                        {eraseEmailSent ? (
                            <Typography variant="body2" color="primary">
                                We've emailed you a link - open it to confirm. It works for 24
                                hours, and nothing is erased until you do.
                            </Typography>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    setEraseError("");
                                    setConfirmEraseOpen(true);
                                }}
                            >
                                Delete my personal data
                            </Button>
                        )}
                    </Paper>
                )}

                <Dialog
                    open={confirmLeaveOpen}
                    onClose={() => !leaving && setConfirmLeaveOpen(false)}
                >
                    <DialogTitle>{team ? `Leave ${team.name}?` : "Leave team?"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You'll lose access to this team's songs and events. Your account
                            isn't deleted, and you can rejoin later with a new invitation link.
                        </DialogContentText>
                        {leaveError && (
                            <Typography variant="body2" color="error">
                                {leaveError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmLeaveOpen(false)} disabled={leaving} autoFocus>
                            Cancel
                        </Button>
                        <Button color="secondary" onClick={leaveTeam} disabled={leaving}>
                            Leave team
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={confirmEraseOpen}
                    onClose={() => !erasing && setConfirmEraseOpen(false)}
                >
                    <DialogTitle>Erase your personal data?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            We'll email a confirmation link to {profile ? profile.email : "your address"}.
                            Nothing is erased until you open that link and confirm. Once you do, it
                            can't be undone.
                        </DialogContentText>
                        {eraseError && (
                            <Typography variant="body2" color="error">
                                {eraseError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmEraseOpen(false)} disabled={erasing} autoFocus>
                            Cancel
                        </Button>
                        <Button color="secondary" onClick={requestErasure} disabled={erasing}>
                            Email me the link
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        </Grid>
    );
};

export default AccountSettings;
